import React, { useState, useEffect } from 'react';
import { Factory, Building2, Warehouse, Cpu, Zap, Droplet, Package, Award, ArrowRight, CheckCircle2, MapPin, Users, TrendingUp } from 'lucide-react';

export default function PlantSelectionLanding() {
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 20,
        y: (e.clientY / window.innerHeight) * 20
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const plants = [
    {
      id: 1,
      name: 'Manufacturing Plant A',
      location: 'Detroit, Michigan',
      icon: Factory,
      type: 'Automotive Manufacturing',
      capacity: '50,000 units/year',
      employees: 450,
      features: ['ISO 9001', 'Lean Manufacturing', 'Industry 4.0'],
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      hoverBorder: 'hover:border-blue-500'
    },
    {
      id: 2,
      name: 'Production Facility B',
      location: 'Houston, Texas',
      icon: Building2,
      type: 'Chemical Processing',
      capacity: '100,000 tons/year',
      employees: 320,
      features: ['Safety Certified', 'Automation', 'Green Energy'],
      color: 'from-emerald-500 to-teal-500',
      bgColor: 'bg-emerald-500/10',
      borderColor: 'border-emerald-500/30',
      hoverBorder: 'hover:border-emerald-500'
    },
    {
      id: 3,
      name: 'Distribution Center C',
      location: 'Atlanta, Georgia',
      icon: Warehouse,
      type: 'Logistics & Warehousing',
      capacity: '500,000 sq ft',
      employees: 280,
      features: ['Climate Control', 'Advanced WMS', '24/7 Operations'],
      color: 'from-orange-500 to-amber-500',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/30',
      hoverBorder: 'hover:border-orange-500'
    },
    {
      id: 4,
      name: 'Tech Manufacturing D',
      location: 'San Jose, California',
      icon: Cpu,
      type: 'Electronics & Semiconductors',
      capacity: '1M chips/month',
      employees: 580,
      features: ['Cleanroom', 'AI Quality Control', 'R&D Integrated'],
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
      hoverBorder: 'hover:border-purple-500'
    },
    {
      id: 5,
      name: 'Power Generation E',
      location: 'Phoenix, Arizona',
      icon: Zap,
      type: 'Renewable Energy',
      capacity: '500 MW',
      employees: 180,
      features: ['Solar + Wind', 'Smart Grid', 'Zero Emission'],
      color: 'from-yellow-500 to-orange-500',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/30',
      hoverBorder: 'hover:border-yellow-500'
    },
    {
      id: 6,
      name: 'Water Treatment F',
      location: 'Seattle, Washington',
      icon: Droplet,
      type: 'Water & Environmental',
      capacity: '50M gallons/day',
      employees: 220,
      features: ['Advanced Filtration', 'EPA Compliant', 'Smart Monitoring'],
      color: 'from-cyan-500 to-blue-500',
      bgColor: 'bg-cyan-500/10',
      borderColor: 'border-cyan-500/30',
      hoverBorder: 'hover:border-cyan-500'
    },
    {
      id: 7,
      name: 'Packaging Plant G',
      location: 'Chicago, Illinois',
      icon: Package,
      type: 'Food & Beverage Packaging',
      capacity: '200,000 units/day',
      employees: 350,
      features: ['HACCP Certified', 'Automated Lines', 'Sustainable Materials'],
      color: 'from-green-500 to-lime-500',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/30',
      hoverBorder: 'hover:border-green-500'
    },
    {
      id: 8,
      name: 'Pharmaceutical Plant H',
      location: 'Boston, Massachusetts',
      icon: Award,
      type: 'Pharmaceutical Manufacturing',
      capacity: '10M doses/year',
      employees: 420,
      features: ['FDA Approved', 'GMP Certified', 'Quality Assured'],
      color: 'from-red-500 to-rose-500',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
      hoverBorder: 'hover:border-red-500'
    }
  ];

  const handlePlantSelect = (plant) => {
    setSelectedPlant(plant);
    setTimeout(() => {
      document.getElementById('cta-section')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const handleLogin = () => {
    if (selectedPlant) {
      alert(`Selected: ${selectedPlant.name}\nProceeding to login...`);
      // window.location.href = `/login?plant=${selectedPlant.id}`;
    } else {
      alert('Please select a plant facility before continuing!');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"
          style={{
            top: '10%',
            left: '5%',
            transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`
          }}
        />
        <div 
          className="absolute w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"
          style={{
            top: '60%',
            right: '10%',
            transform: `translate(${-mousePosition.x}px, ${-mousePosition.y}px)`
          }}
        />
        <div 
          className="absolute w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl"
          style={{
            bottom: '10%',
            left: '40%',
            transform: `translate(${mousePosition.y}px, ${mousePosition.x}px)`
          }}
        />
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-40" />

      <div className="relative z-10 container mx-auto px-4 py-8 md:py-12">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16 animate-fade-in">
          <div className="inline-flex items-center gap-3 mb-4">
            <Factory className="w-12 h-12 text-blue-400" />
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
              IndustryHub
            </h1>
          </div>
          <p className="text-blue-300 text-sm md:text-base tracking-widest uppercase mb-6">Enterprise Plant Management</p>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
            Select Your Plant Facility
          </h2>
          <p className="text-slate-300 text-lg md:text-xl max-w-2xl mx-auto">
            Choose from our network of world-class manufacturing and production facilities
          </p>
        </div>

        {/* Plants Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
          {plants.map((plant, index) => {
            const Icon = plant.icon;
            const isSelected = selectedPlant?.id === plant.id;
            
            return (
              <div
                key={plant.id}
                onClick={() => handlePlantSelect(plant)}
                className={`
                  group relative cursor-pointer rounded-2xl border-2 p-6 
                  backdrop-blur-sm transition-all duration-500 ease-out
                  ${plant.bgColor} ${isSelected ? plant.hoverBorder : plant.borderColor}
                  hover:scale-105 hover:shadow-2xl hover:-translate-y-2
                  ${isSelected ? 'ring-4 ring-offset-2 ring-offset-slate-900' : ''}
                  animate-slide-up
                `}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Selection Indicator */}
                {isSelected && (
                  <div className="absolute -top-3 -right-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full p-2 shadow-lg animate-bounce-in">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                )}

                {/* Icon with Gradient Background */}
                <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${plant.color} mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className="w-10 h-10 text-white" />
                </div>

                {/* Plant Info */}
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-blue-400 group-hover:to-cyan-400 transition-all">
                  {plant.name}
                </h3>
                
                <div className="flex items-center gap-2 text-slate-400 text-sm mb-3">
                  <MapPin className="w-4 h-4" />
                  <span>{plant.location}</span>
                </div>

                <p className="text-slate-300 text-sm mb-4 font-medium">{plant.type}</p>

                {/* Stats */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                    <span className="text-white font-semibold">{plant.capacity}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <Users className="w-4 h-4 text-blue-400" />
                    <span className="text-white font-semibold">{plant.employees} Employees</span>
                  </div>
                </div>

                {/* Features */}
                <div className="flex flex-wrap gap-2">
                  {plant.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-white/10 text-slate-200 border border-white/20"
                    >
                      {feature}
                    </span>
                  ))}
                </div>

                {/* Hover Effect Overlay */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${plant.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div id="cta-section" className="text-center max-w-2xl mx-auto">
          {selectedPlant && (
            <div className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-2 border-blue-500/50 backdrop-blur-sm animate-slide-up">
              <div className="flex items-center justify-center gap-3 mb-2">
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                <span className="text-white font-semibold text-lg">Selected Plant</span>
              </div>
              <p className="text-2xl font-bold text-white">{selectedPlant.name}</p>
              <p className="text-slate-300 mt-1">{selectedPlant.location} • {selectedPlant.type}</p>
            </div>
          )}

          <button
            onClick={handleLogin}
            className="group relative inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-500 via-cyan-500 to-emerald-500 text-white font-bold text-lg rounded-full shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 hover:scale-105 overflow-hidden"
          >
            <span className="relative z-10">Continue to Login</span>
            <ArrowRight className="w-6 h-6 relative z-10 group-hover:translate-x-1 transition-transform" />
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-cyan-500 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </button>

          {!selectedPlant && (
            <p className="text-slate-400 text-sm mt-4">Please select a plant facility to continue</p>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounce-in {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
          }
        }

        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }

        .animate-slide-up {
          animation: slide-up 0.6s ease-out backwards;
        }

        .animate-bounce-in {
          animation: bounce-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}